import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import axios from "axios";
import AddToHomeBanner from "@/components/AddToHomeBanner";
import BirthdayBanner from "@/components/BirthdayBanner";
import { useCmsContent, substitutePricingTokens } from "@/lib/cms";
import { BACKEND_URL } from "@/lib/api";
import {
  Footprints, Calendar, Sparkles, Star, MapPin, ShieldCheck, Trash2, ArrowRight,
} from "lucide-react";

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, delay, ease: "easeOut" },
});

const STEP_ICONS = [Calendar, ShieldCheck, Footprints];
const REWARD_ICONS = [Trash2, Sparkles, MapPin, Star];

export default function Home() {
  const { c } = useCmsContent("home");
  const [settings, setSettings] = useState({});

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/settings`).then((r) => setSettings(r.data || {})).catch(() => {});
  }, []);

  const ctaPrimary = c["home.hero.cta_primary"] || {};
  const ctaSecondary = c["home.hero.cta_secondary"] || {};
  const pricingCta = c["home.pricing.cta"] || {};
  const rewardsCta = c["home.rewards.cta"] || {};
  const bottomCta = c["home.cta.button"] || {};
  const chips = c["home.hero.chips"] || [];
  const steps = c["home.how.steps"] || [];
  const rewardTiles = c["home.rewards.tiles"] || [];
  const extraNote = substitutePricingTokens(c["home.pricing.extra_note"] || "", settings);
  const addonNote = substitutePricingTokens(c["home.pricing.addon_note"] || "", settings);
  const oneBagPrice = `$${settings.price_1_bag ?? 3}`;
  const twoBagPrice = `$${settings.price_2_bags ?? 5}`;

  return (
    <div className="overflow-hidden">
      {/* HERO */}
      <section className="relative">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-gradient-to-b from-green-50 via-amber-50/40 to-white" />
        </div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6 pb-16 sm:pb-24">
          <div className="mb-4"><AddToHomeBanner /></div>
          <div className="mb-4"><BirthdayBanner compact /></div>
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <motion.span
                {...fade(0)}
                className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 border-2 border-amber-200 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider"
                data-testid="hero-badge"
              >
                <Sparkles size={14} strokeWidth={2.5} /> {c["home.hero.badge"]}
              </motion.span>
              <motion.h1
                {...fade(0.05)}
                className="font-fredoka text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.05] mt-4"
                data-testid="hero-title"
              >
                {c["home.hero.title_lead"]}{" "}
                <span className="text-green-600">{c["home.hero.title_brand"]}</span>{" "}
                {c["home.hero.title_tail"]}
              </motion.h1>
              <motion.p {...fade(0.12)} className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl">
                {c["home.hero.paragraph"]}
              </motion.p>
              <motion.div {...fade(0.2)} className="mt-7 flex flex-wrap gap-3">
                <Link to={ctaPrimary.href || "/schedule"} className="btn-push-green px-6 py-3 text-base inline-flex items-center gap-2" data-testid="hero-schedule-btn">
                  {ctaPrimary.label || "Schedule Pickup"} <ArrowRight size={18} strokeWidth={2.5} />
                </Link>
                <Link to={ctaSecondary.href || "/rules"} className="btn-push-outline px-6 py-3 text-base" data-testid="hero-rules-btn">
                  {ctaSecondary.label || "Service Rules"}
                </Link>
              </motion.div>
              <motion.div {...fade(0.25)} className="mt-4">
                <Link
                  to="/quick-pay"
                  className="group inline-flex items-center justify-center gap-2 w-full sm:w-auto rounded-2xl bg-amber-400 hover:bg-amber-500 text-slate-900 border-b-4 border-amber-700 px-5 py-3 text-base font-fredoka font-bold shadow-sm hover:shadow transition-all active:translate-y-[1px] active:border-b-2"
                  data-testid="hero-quickpay-link"
                >
                  <span className="text-xl leading-none">⚡</span>
                  Kids already at your door? Quick Pay
                  <ArrowRight size={18} strokeWidth={2.5} className="group-hover:translate-x-0.5 transition-transform" />
                </Link>
                <div className="text-xs text-slate-500 mt-1.5">No login, no scheduling — pay them on the spot.</div>
              </motion.div>

              <motion.div {...fade(0.28)} className="mt-8 flex flex-wrap gap-4 text-sm text-slate-600">
                {chips[0] && <div className="flex items-center gap-2"><Calendar size={16} className="text-green-600" strokeWidth={2.5}/>{chips[0]}</div>}
                {chips[1] && <div className="flex items-center gap-2"><ShieldCheck size={16} className="text-green-600" strokeWidth={2.5}/>{chips[1]}</div>}
                {chips[2] && <div className="flex items-center gap-2"><Star size={16} className="text-amber-500" strokeWidth={2.5}/>{chips[2]}</div>}
              </motion.div>
            </div>

            <motion.div {...fade(0.15)} className="relative">
              <div className="card-clay p-6 sm:p-8 relative overflow-hidden">
                <div className="absolute -top-6 -right-6 w-32 h-32 rounded-full bg-amber-200/60 blur-2xl" />
                <div className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full bg-green-200/60 blur-2xl" />
                <h3 className="font-fredoka text-2xl font-bold text-slate-900">{c["home.pricing.title"]}</h3>
                <p className="text-sm text-slate-500 mt-1">{c["home.pricing.subtitle"]}</p>
                <div className="mt-5 grid grid-cols-2 gap-3">
                  <PriceTile bags={c["home.pricing.one_bag_label"]} price={oneBagPrice} />
                  <PriceTile bags={c["home.pricing.two_bag_label"]} price={twoBagPrice} highlight />
                </div>
                <div className="mt-3 rounded-2xl border-2 border-dashed border-slate-200 p-3 text-sm text-slate-600">
                  {extraNote}
                </div>
                <div className="mt-3 rounded-2xl bg-amber-50 border-2 border-amber-100 p-3 text-sm">
                  <span className="font-bold text-amber-800">Add-on:</span> {addonNote}
                </div>
                <Link to={pricingCta.href || "/schedule"} className="btn-push-amber w-full mt-5 py-3 text-center block" data-testid="card-schedule-btn">
                  {pricingCta.label || "Schedule a Pickup"}
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="font-fredoka text-3xl sm:text-4xl font-bold text-slate-900">{c["home.how.title"]}</h2>
          <p className="text-slate-600 mt-2">{c["home.how.subtitle"]}</p>
          <div className="grid sm:grid-cols-3 gap-5 mt-8">
            {steps.map((s, i) => {
              const Icon = STEP_ICONS[i] || Calendar;
              return (
                <Step key={i} num={String(i + 1)} icon={<Icon size={22} strokeWidth={2.5}/>} title={s.title}>
                  {s.body}
                </Step>
              );
            })}
          </div>
        </div>
      </section>

      {/* REWARDS */}
      <section className="py-12 sm:py-16 bg-slate-900 text-white relative grain overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <span className="inline-flex items-center gap-2 bg-amber-400 text-slate-900 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider">
                <Star size={14} strokeWidth={2.5}/> {c["home.rewards.eyebrow"]}
              </span>
              <h2 className="font-fredoka text-3xl sm:text-4xl font-bold mt-4">
                {c["home.rewards.title"]}
              </h2>
              <p className="text-slate-300 mt-3">
                {c["home.rewards.body"]}
              </p>
              <Link to={rewardsCta.href || "/register"} className="btn-push-amber inline-block mt-6 px-6 py-3" data-testid="rewards-cta">
                {rewardsCta.label || "Start earning points"}
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {rewardTiles.map((label, i) => {
                const Icon = REWARD_ICONS[i] || Star;
                return <RewardTile key={i} icon={<Icon size={20} strokeWidth={2.5}/>} label={label}/>;
              })}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="font-fredoka text-3xl sm:text-4xl font-bold text-slate-900">{c["home.cta.title"]}</h2>
          <p className="text-slate-600 mt-3">{c["home.cta.body"]}</p>
          <Link to={bottomCta.href || "/schedule"} className="btn-push-green inline-block mt-6 px-8 py-4 text-lg" data-testid="cta-schedule-btn">
            {bottomCta.label || "Schedule a Pickup"}
          </Link>
        </div>
      </section>
    </div>
  );
}

const PriceTile = ({ bags, price, highlight }) => (
  <div className={`rounded-2xl p-4 border-2 ${highlight ? "bg-green-50 border-green-200" : "bg-slate-50 border-slate-200"}`}>
    <div className="text-xs uppercase font-bold tracking-wider text-slate-500">{bags}</div>
    <div className="font-fredoka text-3xl font-bold text-slate-900 mt-1">{price}</div>
  </div>
);

const Step = ({ num, icon, title, children }) => (
  <div className="card-clay p-6 relative">
    <div className="absolute -top-3 -left-3 w-10 h-10 rounded-2xl bg-amber-400 border-b-4 border-amber-700 text-white font-fredoka font-bold flex items-center justify-center">{num}</div>
    <div className="w-12 h-12 rounded-2xl bg-green-100 text-green-700 flex items-center justify-center">{icon}</div>
    <h3 className="font-fredoka text-xl font-bold mt-4 text-slate-900">{title}</h3>
    <p className="text-slate-600 mt-1 text-sm leading-relaxed">{children}</p>
  </div>
);

const RewardTile = ({ icon, label }) => (
  <div className="rounded-2xl bg-white/5 border-2 border-white/10 p-4 flex items-center gap-3 backdrop-blur">
    <div className="w-10 h-10 rounded-xl bg-amber-400 text-slate-900 flex items-center justify-center">{icon}</div>
    <div className="font-semibold text-sm">{label}</div>
  </div>
);
